/**
 * Canonical resume-date parsing — the browser mirror of `shared/dates.py`.
 *
 * The editor needs this BEFORE the save round-trip so it can highlight the box
 * the user is looking at rather than reporting a server finding after the fact.
 * That means two implementations of one grammar, which is a drift hazard — so
 * both are driven by the SAME vector file, `tests/date_parse_vectors.json`
 * (pytest: infrastructure/tests/test_dates.py, vitest:
 * webapp/src/__tests__/dateParse.test.ts). Change the grammar in ONE place and
 * both suites go red until the other side catches up. Add cases to the vector
 * file first.
 *
 * Raw date text is never rewritten by this module. It is the display source in
 * the editor, the HTML resume and the PDF; all we do is read it.
 */

export interface ParsedDate {
  /** 'yyyy' | 'yyyy-mm' | 'yyyy-mm-dd' — precision preserved, never invented. */
  iso: string | null;
  /** The string says "I'm still here" — a flag, not a date. */
  current: boolean;
  /** We couldn't read it, or could only read it by guessing. Blocks a save. */
  uncertain: boolean;
  /** Human-readable explanation when `uncertain`. */
  reason: string | null;
}

export const YEAR_MIN = 1900;
export const YEAR_MAX = 2100;
export const FORMAT_HINT = 'try "June 2022" or "2022"';

const MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];
const MONTHS: Record<string, number> = {};
MONTH_NAMES.forEach((name, i) => {
  MONTHS[name] = i + 1;
  MONTHS[name.slice(0, 3)] = i + 1;
});
MONTHS.sept = 9;

const SEASONS: Record<string, number> = {
  winter: 1, spring: 4, summer: 7, fall: 10, autumn: 10,
};
const QUARTER_MONTH: Record<number, number> = { 1: 1, 2: 4, 3: 7, 4: 10 };
const HALF_MONTH: Record<number, number> = { 1: 1, 2: 7 };

/**
 * "This is my current job" strings — NOT dates. Deliberately generous: users
 * type whatever reads naturally into an End-date box. A marker NEVER blocks a
 * save; the editor clears the box and ticks "Current" instead.
 * Keep in lockstep with `_CURRENT_MARKER_PHRASES` in shared/dates.py.
 */
export const CURRENT_MARKERS = [
  'present', 'presently', 'present day', 'present time', 'the present',
  'at present', 'to present', 'to the present', 'to present day',
  'til present', 'till present', 'until present', 'up to present',
  'current', 'currently', 'current role', 'current position', 'current job',
  'current employer', 'currently employed', 'currently working',
  'currently here', 'currently in role', 'my current role',
  'now', 'to now', 'til now', 'till now', 'until now', 'up to now',
  'up till now', 'upto now', 'right now', 'as of now',
  'today', 'to today', 'til today', 'till today', 'until today',
  'to date', 'todate', 'to this date', 'til date', 'till date',
  'until date', 'up to date', 'up-to-date',
  'ongoing', 'on going', 'in progress', 'continuing', 'continues',
  'active', 'still active', 'still employed', 'still here',
  'still working', 'still there', 'still in role', 'still in position',
  'n/a - current', 'n/a current', 'na current', 'n/a - present',
  'n/a present', 'na present',
  'employed', 'working', 'here',
  'open ended', 'open-ended', 'no end date',
];

/** Strings meaning "nothing here" — treated as blank, never uncertain. */
const EMPTY_MARKERS = [
  '', '-', '--', 'n a', 'na', 'none', 'nil', 'null', 'tbd', 'tba',
  'unknown', 'unspecified', 'not applicable',
];

const LEADING_NOISE =
  /^(?:since|from|starting|started|as\s+of|circa|c\.|approx\.?|approximately|about|~)\s+/i;
const TRAILING_PAREN = /\s*\([^()]*\)\s*$/;
const YEAR_TOKEN = /\b(1[89]\d{2}|20\d{2}|2100)\b/g;

function markerKey(value: unknown): string {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

const CURRENT_KEYS = new Set(CURRENT_MARKERS.map(markerKey));
const EMPTY_KEYS = new Set(EMPTY_MARKERS.map(markerKey));

export function isCurrentMarker(value: unknown): boolean {
  return CURRENT_KEYS.has(markerKey(value));
}

function result(
  iso: string | null = null, current = false, uncertain = false, reason: string | null = null,
): ParsedDate {
  return { iso, current, uncertain, reason };
}

function unreadable(raw: string): ParsedDate {
  return result(null, false, true, `Couldn't read "${raw}" as a date — ${FORMAT_HINT}`);
}

const validYear = (y: number) => y >= YEAR_MIN && y <= YEAR_MAX;
const pad = (n: number, width: number) => String(n).padStart(width, '0');
const isoY = (y: number) => pad(y, 4);
const isoYm = (y: number, m: number) => `${pad(y, 4)}-${pad(m, 2)}`;
const isoYmd = (y: number, m: number, d: number) => `${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`;

function daysInMonth(y: number, m: number): number {
  if (m === 2) return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

/** Expand a 2-digit year. <50 → 2000s, else 1900s (resume-appropriate). */
const twoDigitYear = (n: number) => (n < 50 ? 2000 + n : 1900 + n);

function allYears(s: string): number[] {
  const out: number[] = [];
  YEAR_TOKEN.lastIndex = 0;
  let m = YEAR_TOKEN.exec(s);
  while (m) {
    const y = parseInt(m[1], 10);
    if (validYear(y)) out.push(y);
    m = YEAR_TOKEN.exec(s);
  }
  return out;
}

/**
 * Parse a free-text resume date into a canonical, variable-precision value.
 * A blank value is NOT uncertain — an empty date field is legitimate.
 */
export function parseDateString(value: unknown): ParsedDate {
  if (value === null || value === undefined) return result();
  const raw = String(value).trim();
  if (!raw) return result();

  const key = markerKey(raw);
  if (CURRENT_KEYS.has(key)) return result(null, true);
  if (EMPTY_KEYS.has(key)) return result();

  let s = raw.replace(/\s+/g, ' ').trim();
  s = stripEnds(s, '.,;:•*"\'');
  let prev: string | null = null;
  while (prev !== s) {
    prev = s;
    s = s.replace(TRAILING_PAREN, '').trim();
    s = s.replace(LEADING_NOISE, '').trim();
  }
  s = stripEnds(s, '.,;:').trim();
  const low = s.toLowerCase();
  if (!low) return result();
  if (CURRENT_KEYS.has(markerKey(low))) return result(null, true);
  if (EMPTY_KEYS.has(markerKey(low))) return result();

  const parsers = [parseNumeric, parseMonthName, parseSeasonQuarter,
    parseBareYear, parseMultiYear, parseLenient];
  for (const parser of parsers) {
    const out = parser(low, raw);
    if (out) return out;
  }
  return unreadable(raw);
}

function stripEnds(s: string, chars: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && chars.includes(s[start])) start += 1;
  while (end > start && chars.includes(s[end - 1])) end -= 1;
  return s.slice(start, end);
}

/** Fully-numeric forms: 2022-06-15, 2022-06, 06/2022, 6/7/2022, 06/22. */
function parseNumeric(s: string, raw: string): ParsedDate | null {
  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (m) {
    const y = +m[1]; const mo = +m[2]; const d = +m[3];
    if (validYear(y) && mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo)) {
      return result(isoYmd(y, mo, d));
    }
    return unreadable(raw);
  }

  m = /^(\d{4})[-/.](\d{1,2})$/.exec(s);
  if (m) {
    const y = +m[1]; const mo = +m[2];
    if (validYear(y) && mo >= 1 && mo <= 12) return result(isoYm(y, mo));
    // "1998-2002" is a RANGE, not a year-month — let the range parser have it.
    return null;
  }

  // mm/yyyy — the form the old parsers silently dropped.
  m = /^(\d{1,2})[-/.](\d{4})$/.exec(s);
  if (m) {
    const mo = +m[1]; const y = +m[2];
    if (validYear(y) && mo >= 1 && mo <= 12) return result(isoYm(y, mo));
    return unreadable(raw);
  }

  // d/m/y or m/d/y — genuinely ambiguous when both components are <= 12.
  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/.exec(s);
  if (m) {
    const a = +m[1]; const b = +m[2];
    const y = m[3].length === 4 ? +m[3] : twoDigitYear(+m[3]);
    if (!validYear(y)) return unreadable(raw);
    let mo: number; let d: number; let uncertain: boolean;
    if (a > 12 && b <= 12) { mo = b; d = a; uncertain = false; }
    else if (b > 12 && a <= 12) { mo = a; d = b; uncertain = false; }
    else if (a <= 12 && b <= 12) { mo = a; d = b; uncertain = a !== b; }
    else return unreadable(raw);
    if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo)) return unreadable(raw);
    if (uncertain) {
      return result(isoYmd(y, mo, d), false, true,
        `"${raw}" could be ${isoYmd(y, mo, d)} or ${isoYmd(y, d, mo)} — write the `
        + 'month out ("June 2022") so it can\'t be misread');
    }
    return result(isoYmd(y, mo, d));
  }

  // mm/yy — "06/22" is Jun 2022 or 22 Jun; there is no way to know.
  m = /^(\d{1,2})[-/.](\d{2})$/.exec(s);
  if (m) {
    const a = +m[1]; const b = +m[2];
    if (a >= 1 && a <= 12) {
      const y = twoDigitYear(b);
      if (validYear(y)) {
        return result(isoYm(y, a), false, true,
          `"${raw}" is ambiguous (a two-digit year, or a day?) — ${FORMAT_HINT}`);
      }
    }
    return unreadable(raw);
  }
  return null;
}

/** June 2022, Jun-2022, June 15 2022, 15 June 2022, 2022 June. */
function parseMonthName(s: string, raw: string): ParsedDate | null {
  let m = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/.exec(s);
  if (m && MONTHS[m[1]]) {
    const mo = MONTHS[m[1]]; const d = +m[2]; const y = +m[3];
    if (validYear(y) && d >= 1 && d <= daysInMonth(y, mo)) return result(isoYmd(y, mo, d));
    return unreadable(raw);
  }

  m = /^(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?([a-z]+)\.?,?\s+(\d{4})$/.exec(s);
  if (m && MONTHS[m[2]]) {
    const d = +m[1]; const mo = MONTHS[m[2]]; const y = +m[3];
    if (validYear(y) && d >= 1 && d <= daysInMonth(y, mo)) return result(isoYmd(y, mo, d));
    return unreadable(raw);
  }

  m = /^([a-z]+)\.?\s*(?:,|-|\/|\s+of)?\s*(\d{4})$/.exec(s);
  if (m && MONTHS[m[1]]) {
    const mo = MONTHS[m[1]]; const y = +m[2];
    if (validYear(y)) return result(isoYm(y, mo));
    return unreadable(raw);
  }

  m = /^(\d{4})\s*[-/,]?\s*([a-z]+)\.?$/.exec(s);
  if (m && MONTHS[m[2]]) {
    const y = +m[1]; const mo = MONTHS[m[2]];
    if (validYear(y)) return result(isoYm(y, mo));
    return unreadable(raw);
  }
  return null;
}

/** Season / quarter / half → year + best-guess month. Deliberately NOT uncertain. */
function parseSeasonQuarter(s: string, _raw: string): ParsedDate | null {
  let m = /^([a-z]+)\.?\s*(?:of\s+)?(\d{4})$/.exec(s) || /^(\d{4})\s+([a-z]+)\.?$/.exec(s);
  if (m) {
    const [a, b] = [m[1], m[2]];
    const isDigits = /^\d+$/.test(a);
    const word = isDigits ? b : a;
    const ytxt = isDigits ? a : b;
    if (SEASONS[word] && validYear(+ytxt)) return result(isoYm(+ytxt, SEASONS[word]));
  }

  m = /^q([1-4])\s*[-/,]?\s*(\d{4})$/.exec(s) || /^(\d{4})\s*[-/,]?\s*q([1-4])$/.exec(s);
  if (m) {
    const [a, b] = [m[1], m[2]];
    const q = a.length === 1 ? +a : +b;
    const y = a.length === 1 ? +b : +a;
    if (validYear(y)) return result(isoYm(y, QUARTER_MONTH[q]));
  }

  m = /^h([12])\s*[-/,]?\s*(\d{4})$/.exec(s) || /^(\d{4})\s*[-/,]?\s*h([12])$/.exec(s);
  if (m) {
    const [a, b] = [m[1], m[2]];
    const h = a.length === 1 ? +a : +b;
    const y = a.length === 1 ? +b : +a;
    if (validYear(y)) return result(isoYm(y, HALF_MONTH[h]));
  }
  return null;
}

function parseBareYear(s: string, raw: string): ParsedDate | null {
  const m = /^(\d{4})$/.exec(s);
  if (!m) return null;
  const y = +m[1];
  return validYear(y) ? result(isoY(y)) : unreadable(raw);
}

/**
 * Ranges ("1998-2002") and multi-values ("2024, 2025") → MAX year. Awards
 * legitimately carry both forms; the latest year dates the entry.
 */
function parseMultiYear(s: string, _raw: string): ParsedDate | null {
  const years = allYears(s);
  return years.length >= 2 ? result(isoY(Math.max(...years))) : null;
}

/**
 * Last resort: exactly one plausible year, optionally with a month name.
 * Reproduces the old parsers' prefix tolerance ("Class of 2010") without
 * inventing precision. No year at all → unreadable, which is what blocks a save.
 */
function parseLenient(s: string, _raw: string): ParsedDate | null {
  const years = allYears(s);
  if (years.length !== 1) return null;
  const y = years[0];
  for (const token of s.split(/[^a-z]+/)) {
    if (MONTHS[token]) return result(isoYm(y, MONTHS[token]));
    if (SEASONS[token]) return result(isoYm(y, SEASONS[token]));
  }
  return result(isoY(y));
}

/**
 * (year, month, day) sort tuple for a canonical value. Year-only sorts as
 * JANUARY — matches shared/dates.py and every resume's existing ordering.
 */
export function sortKey(iso: string | null, current = false, currentSortsLast = true): [number, number, number] {
  if (current) return currentSortsLast ? [9999, 12, 31] : [0, 0, 0];
  if (!iso) return [0, 0, 0];
  const parts = String(iso).split('-');
  const y = parseInt(parts[0], 10);
  if (Number.isNaN(y)) return [0, 0, 0];
  return [y, parts.length > 1 ? +parts[1] : 1, parts.length > 2 ? +parts[2] : 1];
}
